import { HomeOutlined, LoadingOutlined, QuestionCircleOutlined, ReadOutlined, SyncOutlined } from '@ant-design/icons'
import { Breadcrumb, Button, Popover, message } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { AppHeader, ColumnLayout } from '@/components'
import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import { useGetKnowledgeGroupQuery, useGetKnowledgePostQuery, useKnowledgeSyncMutation } from '@/generated/graphql'
import { routes } from '@/routes'

import { useIsHR, useIsSystemAdmin } from '../admin/admin.hooks'
import { KnowledgeCard } from './knowledge-card/knowledge-card.component'
import { Knowledges } from './knowledge-main-group-knowledges.component'
import { KnowledgeUserPage } from './knowledge-main-user-page.component'
import s from './knowledge.module.less'

export const KnowledgeContainer: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const categoryMatch = useRouteMatch<{ categoryId: string }>('/knowledgebase/categories/:categoryId')
  const knowledgeMatch = useRouteMatch<{ knowledgeId: string }>('/knowledgebase/knowledge/:knowledgeId')
  const categoryId = parseInt(categoryMatch?.params.categoryId || '')
  const knowledgePostId = parseInt(knowledgeMatch?.params.knowledgeId || '')
  const { data } = useGetKnowledgeGroupQuery({
    skip: isNaN(categoryId),
    variables: {
      knowledgeGroupId: categoryId,
    },
  })

  const { data: postData } = useGetKnowledgePostQuery({
    skip: isNaN(knowledgePostId),
    variables: {
      knowledgePostId,
    },
  })

  const [knowlegeSync, { loading }] = useKnowledgeSyncMutation()

  const isFeatureToggleOn = useIsFeatureToggleOn('knowledgeLibrary')

  const isSystemAdmin = useIsSystemAdmin()
  const isHR = useIsHR()

  if (!isFeatureToggleOn || (!isNaN(knowledgePostId) && !postData)) {
    return null
  }

  const breadcrumb = data && (
    <div className={s.breadcrumb}>
      <Breadcrumb separator='>'>
        <Breadcrumb.Item onClick={() => history.replace(routes.knowledgebase.home())}>
          <HomeOutlined />
        </Breadcrumb.Item>
        {data.knowledge.group.parentGroups?.map((knowledgeGroup) => (
          <Breadcrumb.Item
            key={knowledgeGroup.knowledgeGroupId}
            onClick={() => history.replace(routes.knowledgebase.category(knowledgeGroup.knowledgeGroupId))}
          >
            {knowledgeGroup.name}
          </Breadcrumb.Item>
        ))}
        <Breadcrumb.Item className={s.lastBreadCrumb}>{data.knowledge.group.name}</Breadcrumb.Item>
      </Breadcrumb>
    </div>
  )

  const sync = (isSystemAdmin || isHR) && (
    <Button
      onClick={() => {
        message.info('开始同步知识，同步过程中请不要再次触发, 请间隔起码 5 分钟再次触发')
        knowlegeSync()
      }}
      icon={loading ? <LoadingOutlined /> : <SyncOutlined />}
    >
      {t('knowledge.sync')}
    </Button>
  )

  const title = (
    <span className={s.title}>
      <span>{t('groups.basics.knowledgeLibrary')}</span>
      <span className={s.culture}>
        {'一站式查找公司内部知识'}
        <Popover
          placement='right'
          content={
            <div>
              <p>1. 企业微信扫码登录</p>
              <p>2. 账号密码登录: 账号为公司邮箱，初始密码为 123456</p>
            </div>
          }
          title='云课堂知识网站登录方式'
        >
          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
        </Popover>
      </span>
    </span>
  )

  const appHeader = (
    <AppHeader
      title={title}
      titleString={t('groups.basics.knowledgeLibrary')}
      avatar={<ReadOutlined style={{ fontSize: 28 }} />}
      actions={sync}
    >
      {breadcrumb}
    </AppHeader>
  )

  const middle = (
    <div className={s.middle}>
      <div className={s.content}>
        {knowledgePostId >= 1 ? (
          <KnowledgeCard knowledgePost={postData!.knowledge.post} type='card' />
        ) : categoryId >= 1 ? (
          <Knowledges knowledgeGroupId={categoryId} />
        ) : (
          <KnowledgeUserPage />
        )}
      </div>
    </div>
  )

  return <ColumnLayout header={appHeader} middle={middle} />
}
